'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { FilterIcon, XIcon, SearchIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { NotificationsFilterParams } from '@/types/notification'
import { NotificationTypeEnum, ReadStatusEnum } from '@/enums'

type PropsType = {
  filters: NotificationsFilterParams
  onFiltersChange: (filters: Partial<NotificationsFilterParams>) => void
  onResetFilters: () => void
}

const Filters = ({ filters, onFiltersChange, onResetFilters }: PropsType) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const { register, handleSubmit, watch, setValue, reset } = useForm<NotificationsFilterParams>({
    defaultValues: filters
  })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchAll = watch()

  const onSubmit = (data: NotificationsFilterParams) => {
    onFiltersChange({ ...data, page: 1 })
    setIsOpen(false)
  }

  const handleClearSearch = () => {
    setValue('q', '')
  }

  const typeOptions: ComboboxOption[] = [
    { label: t(NotificationTypeEnum.INFO), value: NotificationTypeEnum.INFO },
    { label: t(NotificationTypeEnum.SUCCESS), value: NotificationTypeEnum.SUCCESS },
    { label: t(NotificationTypeEnum.WARNING), value: NotificationTypeEnum.WARNING },
    { label: t(NotificationTypeEnum.ERROR), value: NotificationTypeEnum.ERROR },
    { label: t(NotificationTypeEnum.SYSTEM), value: NotificationTypeEnum.SYSTEM }
  ]

  const readStatusOptions: ComboboxOption[] = [
    { label: t(ReadStatusEnum.READ), value: 'true' },
    { label: t(ReadStatusEnum.UNREAD), value: 'false' }
  ]

  const hasActiveFilters = () => {
    return watchAll.q || (watchAll.types && watchAll.types.length > 0) || watchAll.read !== undefined
  }

  return (
    <div className="flex items-center justify-end">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            <FilterIcon className="h-4 w-4" />
            <span className="hidden flex-row items-center justify-items-center lg:flex">{t('filters')}</span>
            {hasActiveFilters() && (
              <span className="bg-primary text-primary-foreground flex h-5 w-5 items-center justify-center rounded-full text-xs">
                !
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('filter_records')}</SheetTitle>
            <SheetDescription>{t('filter_description')}</SheetDescription>
          </SheetHeader>

          <ScrollArea>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4">
              {/** KEYWORD FILTER */}
              <div className="space-y-2">
                <Label htmlFor="filter-q">{t('keyword')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('keyword')}
                    id="filter-q"
                    {...register('q')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    icon={<SearchIcon className="h-4 w-4" />}
                    className="pr-8"
                  />
                  {watchAll.q && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1.5 right-1 h-6 w-6 p-0"
                      onClick={handleClearSearch}
                      type="button">
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {/** TYPE FILTER */}
                <div className="space-y-2">
                  <Label>{t('type')}</Label>
                  <Combobox
                    options={typeOptions}
                    value={watchAll.types || []}
                    onValueChange={value => setValue('types', value as string[])}
                    placeholder={t('select')}
                    multiple
                    clearable
                  />
                </div>

                {/** READ STATUS FILTER */}
                <div className="space-y-2">
                  <Label>{t('status')}</Label>
                  <Combobox
                    options={readStatusOptions}
                    value={watchAll.read !== undefined ? (watchAll.read ? 'true' : 'false') : ''}
                    onValueChange={value => {
                      if (value === 'true') {
                        setValue('read', true)
                      } else if (value === 'false') {
                        setValue('read', false)
                      } else {
                        setValue('read', undefined)
                      }
                    }}
                    placeholder={t('select')}
                    clearable
                  />
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Button type="submit" variant="default" className="flex-1">
                  <FilterIcon className="h-4 w-4" />
                  {t('filter')}
                </Button>
                <Button
                  onClick={() => {
                    reset()
                    onResetFilters()
                    setIsOpen(false)
                  }}
                  variant="destructive"
                  className="flex-1"
                  type="button">
                  <XIcon className="h-4 w-4" />
                  {t('clear_filters')}
                </Button>
              </div>
            </form>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default Filters
